import { useState, useEffect, useCallback } from "react";
import { apiClient } from "@/services/apiClient";

export interface UtilizationByType {
  total: number;
  utilized: number;
  rate: number;
}

export interface CareScoreDetails {
  obligationsOnTime: number;
  obligationsTotal: number;
  hasCurrentReport: boolean;
  hasPrevReport: boolean;
  recentPublishFailures: number;
  recentPostsCount: number;
  publishedPosts30d: number;
  scheduledPostsCount: number;
  pipelineCount: number;
  draftCount: number;
  totalMedia: number;
  utilizedMedia: number;
  utilizationRate: number;
  utilizationByType: Record<string, UtilizationByType>;
  captionlessReadyPosts: number;
  daysSinceLastContact: number;
  followerGrowth: number;
  followerGrowthPct: number;
  currentFollowers: number;
  avgEngagement: number;
  topEngagement: number;
  postsWithEngagement: number;
  activeBriefs: number;
  pendingApprovals: number;
  ideasInPipeline: number;
  approvedIdeas: number;
  approvalReadyPosts: number;
  staleApprovals: number;
  briefFreshness: string;
  ideaRejectionRate: number;
  avgApprovalDays: number;
  runwayWeeks: number;
  publishedThisWeek: number;
  scheduledCount: number;
  readyPostsCount: number;
  postsPerWeek: number;
  postsPerWeekSet: boolean;
  maxGapDays: number;
  calendarEventsUncovered: number;
  uncoveredEventNames: string[];
  captionlessScheduled: number;
  weeklyCoverage: Array<{
    weekStart: string;
    count: number;
    target: number;
    covered: boolean;
  }>;
  firstShortWeek: {
    weekStart: string;
    count: number;
    target: number;
    covered: boolean;
  } | null;
}

export interface CareScoreData {
  score: number;
  serviceScore: number;
  contentScore: number;
  runwayScore: number;
  contactScore: number;
  growthScore: number;
  planningScore: number;
  velocityScore: number;
  details: CareScoreDetails;
}

export interface CareScoreSnapshot {
  date: string;
  score: number;
  serviceScore: number;
  contentScore: number;
  runwayScore: number;
  contactScore: number;
  growthScore: number;
  planningScore: number;
}

export interface ScoreTrend {
  delta: number;
  period: string;
  previousScore: number;
}

interface UseCareScoreOptions {
  clientId: string;
}

export function useCareScore({ clientId }: UseCareScoreOptions) {
  const [data, setData] = useState<CareScoreData | null>(null);
  const [trend, setTrend] = useState<ScoreTrend | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [scoreResponse, historyResponse] = await Promise.all([
        apiClient.get<CareScoreData & { success: boolean }>(
          `/api/clients/${clientId}/care-score`,
        ),
        apiClient
          .get<{ history: CareScoreSnapshot[] }>(
            `/api/clients/${clientId}/care-score/history`,
          )
          .catch(() => ({ history: [] })),
      ]);

      setData({
        score: scoreResponse.score,
        serviceScore: scoreResponse.serviceScore,
        contentScore: scoreResponse.contentScore,
        runwayScore: scoreResponse.runwayScore,
        contactScore: scoreResponse.contactScore,
        growthScore: scoreResponse.growthScore,
        planningScore: scoreResponse.planningScore,
        velocityScore: scoreResponse.velocityScore,
        details: scoreResponse.details,
      });

      const history = historyResponse.history || [];
      if (history.length >= 2) {
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 8);
        const weekAgoStr = weekAgo.toISOString().slice(0, 10);

        const older = history.find((s) => s.date <= weekAgoStr);
        if (older) {
          setTrend({
            delta: scoreResponse.score - older.score,
            period: "7d",
            previousScore: older.score,
          });
        } else {
          const oldest = history[history.length - 1];
          setTrend({
            delta: scoreResponse.score - oldest.score,
            period: "recent",
            previousScore: oldest.score,
          });
        }
      } else {
        setTrend(null);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load care score",
      );
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const logContact = useCallback(
    async (notes?: string) => {
      await apiClient.post(`/api/clients/${clientId}/log-contact`, {
        notes: notes || "",
      });
      await fetchAll();
    },
    [clientId, fetchAll],
  );

  return { data, trend, loading, error, refresh: fetchAll, logContact };
}
